import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LeaderboardPlayAgain } from '../../components/LeaderboardPlayAgain.tsx';
import { gameService } from '../../services/gameService.ts';
import { GameResult } from '../../types.ts';
import { FIRESWEEPER_CONTAINER } from '../../constants/gameModes.ts';
import { useUtcCountdown } from '../../hooks/useUtcCountdown.ts';
import { ContainerLeaderboardLocationState } from '../../contexts/GameContext.tsx';
import { sortContainerLeaderboard } from '../../utils/containerLeaderboard.ts';
import { buildContainerLeaderboardRows } from '../../utils/leaderboardDisplay.ts';

function LeaderboardRow({
  rank,
  result,
  highlight,
}: {
  rank: number | string;
  result: GameResult;
  highlight?: boolean;
}) {
  return (
    <tr className={highlight ? 'leaderboard-row-player' : undefined}>
      <td>{rank}</td>
      <td>
        <span className={`badge ${highlight ? 'badge-player' : 'badge-survival'}`}>
          {result.survivalPercentage.toFixed(1)}%
        </span>
      </td>
      <td>{result.duration}</td>
      <td>{result.preBurns}</td>
    </tr>
  );
}

export default function SurvivalLeaderboard() {
  const location = useLocation();
  const playerResult = (location.state as ContainerLeaderboardLocationState | null)?.playerResult;
  const [leaderboard, setLeaderboard] = useState<GameResult[]>([]);
  const [gamesPlayed, setGamesPlayed] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const { dayKey, countdown } = useUtcCountdown();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [results, count] = await Promise.all([
          gameService.getSurvivalLeaderboard(500, dayKey),
          gameService.getSurvivalGamesCount(dayKey),
        ]);
        setLeaderboard(results);
        setGamesPlayed(count);
      } catch (error) {
        console.error('Error fetching survival leaderboard:', error);
        setGamesPlayed(0);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dayKey]);

  const sorted = useMemo(() => sortContainerLeaderboard(leaderboard), [leaderboard]);
  const displayRows = useMemo(
    () => buildContainerLeaderboardRows(sorted, playerResult),
    [sorted, playerResult]
  );

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="leaderboard-page">
      <div className="leaderboard-header">
        <h1 className="leaderboard-title">Today&apos;s {FIRESWEEPER_CONTAINER.subtitle} Board</h1>
        <p className="leaderboard-subtitle">
          {dayKey} UTC · Resets in {countdown}
        </p>
        <p className="leaderboard-plays">
          {gamesPlayed !== null ? (
            `${gamesPlayed.toLocaleString()} plays today`
          ) : (
            <span className="stat-shimmer" aria-label="Loading plays today" />
          )}
        </p>
        <p className="leaderboard-hint">{FIRESWEEPER_CONTAINER.leaderboardHint}</p>
      </div>

      <LeaderboardPlayAgain to={FIRESWEEPER_CONTAINER.path} />

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Rank</th>
              <th>Survival</th>
              <th>Duration(s)</th>
              <th>Pre-Burns</th>
            </tr>
          </thead>
          <tbody>
            {displayRows.length === 0 ? (
              <tr>
                <td colSpan={4} className="table-empty">
                  No scores yet today. Be the first to play!
                </td>
              </tr>
            ) : (
              displayRows.map((row) => (
                <LeaderboardRow
                  key={row.key}
                  rank={row.rank}
                  result={row.result}
                  highlight={row.highlight}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
